import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Divider, Descriptions, Button, Spin } from 'antd';
import { taxTraxTaxBillsClear } from './TaxTraxTaxBills.actions';
import {
  BasicDrawer,
  CommonTable,
  Sizes,
  LOADING,
  ExportFormatNames,
} from 'common';
import { TaxTraxTaxBillsResponse, TaxBill } from '../models';
import { AppState } from 'store';
import { formatDate, getAccessToken } from 'common/helpers';
import Column from 'antd/lib/table/Column';
import { taxBillRecordSelector, taxBillSelector } from 'pages/TaxTrax';
import styles from '../TaxTrax.module.scss';
import {
  downloadTaxBillHtmlAsync,
  downloadTaxBillPdfAsync,
  fetchTaxTraxTaxBillRecordAsync,
} from '../TaxTraxTaxBillRecord';
import TaxTraxTaxBillRecord from '../TaxTraxTaxBillRecord/TaxTraxTaxBillRecord';
import {
  PropertyInformationDescriptions,
  PropertyIdentifierDescriptions,
  PropertyOwnerDescriptions,
} from 'common/templates';
import { Property } from 'common/models';
import { TaxTraxDisclaimer } from '../templates';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const TaxTraxTaxBills: React.FC<{
  taxBillsResponse: TaxTraxTaxBillsResponse;
  property: Property;
}> = (props: {
  taxBillsResponse: TaxTraxTaxBillsResponse;
  property: Property;
}) => {
  const dispatch = useDispatch();
  const { taxBillsResponse, property } = props;
  const { taxBills, propertyInformation, propertyIdentifier } =
    taxBillsResponse;

  const {
    taxBillListLoading,
    taxBillRecordLoading,
    taxBillRecord,
    taxBill,
  }: any = useSelector((state: AppState) => ({
    login: state.login,
    taxBillListLoading: state.taxTraxTaxBills.loading,
    taxBillRecordLoading: state.taxTraxTaxBillRecord.loading,
    taxBillRecord: taxBillRecordSelector(state),
    taxBill: taxBillSelector(state),
  }));

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const handleSearch = (taxBill: TaxBill) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken) {
          dispatch(
            fetchTaxTraxTaxBillRecordAsync(
              taxBill,
              property,
              response.accessToken,
            ),
          );
        }
      });
    }
  };

  const downloadBillRequest = (
    token: string,
    taxBill: TaxBill,
    fileFormat: ExportFormatNames,
  ) => {
    if (fileFormat === ExportFormatNames.HTML)
      dispatch(downloadTaxBillHtmlAsync(taxBill, property.hob, token));
    else if (fileFormat === ExportFormatNames.PDF)
      dispatch(downloadTaxBillPdfAsync(taxBill, property.hob, token));
  };

  const downloadBill = (taxBill: TaxBill, fileFormat: ExportFormatNames) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          downloadBillRequest(response.accessToken, taxBill, fileFormat);
      });
    }
  };

  // group the bills by their tax type and store the approximate release date for each group
  const taxBillGroups = taxBills.reduce(
    (groups: any, taxBill: TaxBill) => ({
      ...groups,
      [taxBill.taxType]: {
        taxBills: [...(groups[taxBill.taxType]?.taxBills || []), taxBill],
        approximateReleaseDate: taxBill.approximateReleaseDate
          ? taxBill.approximateReleaseDate
          : groups[taxBill.taxType]?.approximateReleaseDate,
        hasAtLeastOneDownloadableBill: groups[taxBill.taxType]?.taxBills?.some(
          (taxBill: TaxBill) => taxBill.billFlag,
        ),
      },
    }),
    {},
  );

  return (
    <BasicDrawer
      title={'Property Details'}
      onBack={() => dispatch(taxTraxTaxBillsClear())}
    >
      <Spin
        spinning={taxBillListLoading || taxBillRecordLoading}
        size={Sizes.LARGE}
        tip={LOADING}
      >
        <PropertyInformationDescriptions
          county={property.county}
          propertyInformation={propertyInformation}
        />
        <PropertyOwnerDescriptions owners={property.owners} />
        <PropertyIdentifierDescriptions
          propertyIdentifier={propertyIdentifier}
          accountNumber={property.apn}
        />

        <Row type="flex" justify="space-between">
          {Object.keys(taxBillGroups).map((groupKey) => {
            return (
              <Col xs={24} xxl={12} key={groupKey}>
                <Descriptions
                  title={taxBillGroups[groupKey].taxBills[0].taxTypeName}
                  bordered
                  size={'small'}
                  column={2}
                >
                  <Descriptions.Item label="Appx Release Date">
                    {taxBillGroups[groupKey].approximateReleaseDate ?? 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
                <CommonTable
                  items={taxBillGroups[groupKey].taxBills}
                  rowKey="index"
                  className={styles.table}
                >
                  <Column title="TAX BILL YEAR" dataIndex="displayYear" />
                  <Column
                    title="LAST UPDATED"
                    dataIndex="postDate"
                    render={(text: string) => formatDate(text)}
                  />
                  <Column
                    title="PAYMENT STATUS"
                    render={(text: string, record: TaxBill) => (
                      <>
                        <Button
                          type="link"
                          size="small"
                          icon="more"
                          onClick={() => handleSearch(record)}
                          title="Click to see payment status"
                        >
                          Details
                        </Button>
                        <p
                          style={{
                            display: 'inline',
                            color: '#40a9ff',
                            fontWeight: 'bold',
                          }}
                        >
                          *
                        </p>
                      </>
                    )}
                  />
                  {taxBillGroups[groupKey].hasAtLeastOneDownloadableBill && (
                    <Column
                      title="BILL PREVIEW"
                      render={(text: string, record: TaxBill) => (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon="html5"
                            onClick={() =>
                              downloadBill(record, ExportFormatNames.HTML)
                            }
                            title={
                              record.billFlag
                                ? 'Click to download bill preview'
                                : 'This bill is not available at this time'
                            }
                            disabled={!record.billFlag}
                          >
                            Html
                          </Button>
                          {record.billFlag ? (
                            <p
                              style={{
                                display: 'inline',
                                color: '#40a9ff',
                                fontWeight: 'bold',
                              }}
                            >
                              *
                            </p>
                          ) : (
                            <>N/A</>
                          )}
                        </>
                      )}
                    />
                  )}
                  {taxBillGroups[groupKey].hasAtLeastOneDownloadableBill && (
                    <Column
                      title="BILL DOWNLOAD"
                      render={(text: string, record: TaxBill) => (
                        <>
                          <Button
                            type="link"
                            size="small"
                            icon="file-pdf"
                            onClick={() =>
                              downloadBill(record, ExportFormatNames.PDF)
                            }
                            title={
                              record.billFlag
                                ? 'Click to download bill'
                                : 'This bill is not available at this time'
                            }
                            disabled={!record.billFlag}
                          >
                            PDF
                          </Button>
                          {record.billFlag ? (
                            <p
                              style={{
                                display: 'inline',
                                color: '#40a9ff',
                                fontWeight: 'bold',
                              }}
                            >
                              *
                            </p>
                          ) : (
                            <>N/A</>
                          )}
                        </>
                      )}
                    />
                  )}
                </CommonTable>
                <p className={styles.table}>
                  <span style={{ color: '#40a9ff', fontWeight: 'bold' }}>
                    *
                  </span>
                  Fee applied on Click
                  <span style={{ color: '#40a9ff', fontWeight: 'bold' }}>
                    *
                  </span>
                </p>
              </Col>
            );
          })}
        </Row>

        {taxBillRecord && taxBill && property && (
          <TaxTraxTaxBillRecord
            taxBillRecord={taxBillRecord}
            taxBill={taxBill}
            property={property}
            propertyInformation={propertyInformation}
            propertyIdentifier={propertyIdentifier}
            handleBillDownload={downloadBill}
          />
        )}

        <Divider dashed />
        <TaxTraxDisclaimer></TaxTraxDisclaimer>
      </Spin>
    </BasicDrawer>
  );
};

export default TaxTraxTaxBills;
